import { useState, useEffect } from 'react';
import { parseISO, isValid } from 'date-fns';
import type { InsightObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute.tsx';
import { AttributeTypeMap } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-types.tsx';
import type { CmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';

export const getDefaultAttributeValue = (attribute?: InsightObjectAttribute): string => {
	if (attribute && attribute.type === 'default') {
		const [defaultValue] = attribute.defaultTypeValues.map(({ value }) => value ?? '');
		return defaultValue;
	}
	return '';
};

export const getDefaultAttributeDateValue = (attribute?: InsightObjectAttribute): string => {
	const defaultValue = getDefaultAttributeValue(attribute);

	// checks if it is a valid date / datetime value
	if (isValid(parseISO(defaultValue))) {
		return defaultValue;
	}

	return '';
};

export const useDefaultAttributeValue = (attribute?: InsightObjectAttribute) => {
	const [value, setValue] = useState<string>(getDefaultAttributeValue(attribute));

	useEffect(() => {
		setValue(getDefaultAttributeValue(attribute));
	}, [attribute]);

	return [value, setValue] as const;
};

export const useDefaultAttributeDateValue = (attribute?: InsightObjectAttribute) => {
	const [value, setValue] = useState<string>(getDefaultAttributeDateValue(attribute));

	useEffect(() => {
		setValue(getDefaultAttributeDateValue(attribute));
	}, [attribute]);

	return [value, setValue] as const;
};

export const getDefaultAttributeArrayValue = (attribute?: InsightObjectAttribute): string[] => {
	if (attribute && attribute.type === 'default') {
		return attribute.defaultTypeValues.map(({ value }) => value ?? '');
	}
	return [];
};

export const getDefaultAttributeArrayValueFromCmdbObjectAttribute = (
	attribute?: CmdbObjectAttribute,
): string[] => {
	if (attribute && attribute.type === AttributeTypeMap.Default) {
		return attribute.objectAttributeValues.map(({ value }) => value ?? '');
	}
	return [];
};

export const useDefaultAttributeArrayValue = (attribute?: InsightObjectAttribute) => {
	const [value, setValue] = useState<string[]>(getDefaultAttributeArrayValue(attribute));

	useEffect(() => {
		setValue(getDefaultAttributeArrayValue(attribute));
	}, [attribute]);

	return [value, setValue] as const;
};

export const getDefaultMultipleAttributeValuesAsString = (
	attribute?: InsightObjectAttribute,
): string => {
	if (attribute && attribute.type === 'default') {
		return attribute.defaultTypeValues.map(({ value }) => value ?? '').join(' ');
	}
	return '';
};

export const useDefaultMultipleAttributeValuesAsString = (attribute?: InsightObjectAttribute) => {
	const [value, setValue] = useState<string>(getDefaultMultipleAttributeValuesAsString(attribute));

	useEffect(() => {
		setValue(getDefaultMultipleAttributeValuesAsString(attribute));
	}, [attribute]);

	return [value, setValue] as const;
};

export const getBooleanAttributeValue = (attribute?: InsightObjectAttribute): boolean | null => {
	if (attribute && attribute.type === 'default') {
		const [{ value }] = attribute.defaultTypeValues;
		if (value === 'true') {
			return true;
		}
		if (value === 'false') {
			return false;
		}
	}
	// Will arrive here if attribute value is 'None'
	return null;
};

export const useBooleanValue = (attribute?: InsightObjectAttribute) => {
	const [value, setValue] = useState<boolean | null>(getBooleanAttributeValue(attribute));

	useEffect(() => {
		setValue(getBooleanAttributeValue(attribute));
	}, [attribute]);

	return [value, setValue] as const;
};
